import { restoreScroll } from '../router.js';
import { backButton } from '../components/branding.js';
import { emptyState, errorCard, loaderView, wireRetry } from '../components/feedback.js';
import { openPhrasePreview } from '../components/phrasePreview.js';
import { phraseRow } from '../components/phraseRow.js';
import { createPlayBar, playBarHtml } from '../components/playBar.js';
import { renderAppScreen } from '../components/shell.js';
import { getDictionaryPhrases, isInDictionary } from '../study/dictionary.js';

const EMPTY = [
  'Słownik jest pusty',
  'Dotknij zwrotu na ekranie tematu i wybierz „Dodaj do słownika”.',
];

/* -------------------------------------------------------------------------- */
/* Screen                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Read the saved ids from the `dictionary` store, resolve each one to its
 * `phrases` row and render the shared phrase rows: the small play button plays one
 * clip in place, the text opens the same preview panel the topic screen opens, and
 * the bottom bar plays the whole list in the order shown (`components/playBar.js`
 * — same button, highlight, autoscroll and resume point as the topic's dialogue).
 *
 * Purely local, like favorites — nothing here reaches the network, so the list
 * re-reads IndexedDB on every visit and picks up whatever the panel changed.
 */
async function loadDictionary(main) {
  const region = main.querySelector('#dictionary-region');
  region.innerHTML = loaderView('Wczytywanie słownika…', { fullscreen: false });

  let phrases = [];
  try {
    phrases = await getDictionaryPhrases();
  } catch (error) {
    region.innerHTML = errorCard(error, 'Nie udało się wczytać słownika.');
    wireRetry(region, () => loadDictionary(main));
    return;
  }

  // Nothing saved → no list and no play bar (it stays hidden until wired).
  if (!phrases.length) {
    region.innerHTML = emptyState(...EMPTY, { fill: true });
    return;
  }

  region.innerHTML = `<ul class="flex flex-col gap-1">${phrases
    .map((phrase) => phraseRow(phrase, phrase.audio_blob))
    .join('')}</ul>`;
  restoreScroll(); // the list is in the DOM — return to where the user left it

  const playback = createPlayBar(main, {
    list: region,
    playlist: () => phrases.map((phrase) => ({ id: phrase.id, blob: phrase.audio_blob })),
    label: 'Odtwórz zwroty',
  });

  const clipFor = (id) => phrases.find((phrase) => phrase.id === id)?.audio_blob;

  /**
   * The panel is where a phrase gets removed from the dictionary, so once it
   * closes drop the row it no longer belongs to — otherwise the list would keep
   * showing (and playing) a phrase the user just took out. Anything else the panel
   * did needs no redraw: the row's own content can't change there.
   */
  const syncRow = async (phraseId) => {
    try {
      if (await isInDictionary(phraseId)) return;
    } catch {
      return; // IndexedDB unavailable — leave the list as it is
    }
    phrases = phrases.filter((phrase) => phrase.id !== phraseId);
    playback.invalidate(); // the playlist just lost an item
    region.querySelector(`[data-phrase="${CSS.escape(phraseId)}"]`)?.remove();
    if (!phrases.length) {
      playback.hide();
      region.innerHTML = emptyState(...EMPTY, { fill: true });
    }
  };

  const openPreview = (phraseId) =>
    openPhrasePreview(phraseId, { onClose: () => syncRow(phraseId) });

  // Both row affordances are real `<button>`s, so Enter/Space arrive here as
  // clicks — this screen needs no keyboard handler of its own.
  region.addEventListener('click', (event) => {
    const playEl = event.target.closest('[data-play-phrase]');
    if (playEl) {
      const id = playEl.dataset.playPhrase;
      playback.playOne(id, clipFor(id)); // becomes the point the bar carries on from
      return;
    }
    const openEl = event.target.closest('[data-phrase-open]');
    if (openEl) openPreview(openEl.dataset.phraseOpen);
  });
}

/** "Słownik" — the phrases saved from the preview panel, read from IndexedDB. */
export function renderPhrases(mount) {
  const content = `
    <div class="flex flex-1 flex-col py-8 pb-28">
      <h1 class="text-2xl font-semibold tracking-tight text-neutral-900">Słownik</h1>
      <p class="mt-1 text-sm leading-relaxed text-neutral-500">Zwroty zapisane na później.</p>
      <section id="dictionary-region" class="mt-6 flex flex-1 flex-col"></section>
    </div>

    <!-- Bottom bar: nothing but the centred play button — it plays the whole list. -->
    ${playBarHtml({ label: 'Odtwórz zwroty' })}`;

  renderAppScreen(mount, {
    active: 'phrases',
    contentHtml: content,
    leading: backButton(),
    onMount(main) {
      loadDictionary(main);
    },
  });
}
